/**
 * 在途监管平台 — 类型定义
 * 对齐 docs/04-API设计/在途监管平台-接口清单.md（#74-97）
 */

export interface ApiEnvelope<T> {
  code: number;
  message?: string;
  data: T;
}

// ===== 认证 =====

/** 用户角色（后端 /auth/login 返回 roles 数组元素） */
export interface UserRole {
  code: string;
  name: string;
  permissions?: string[];
}

/** 登录用户 */
export interface User {
  id: number;
  username: string;
  real_name: string;
  phone?: string;
  /** 角色编码（后端 /auth/login 返回 []string，如 ["gov","enterprise","driver"]） */
  roles?: string[];
  /** N6: 登录主体身份。user=平台用户（/auth/login）；driver=司机档案独立登录（/driver/auth/login） */
  identity?: 'user' | 'driver';
}

/** 登录成功响应（/auth/login data 字段） */
export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

/** 登录结果（与 TokenResponse 同构，页面持久化用） */
export type LoginResult = TokenResponse;

/** 司机独立登录成功响应（POST /driver/auth/login data 字段，N6） */
export interface DriverLoginResult {
  token: string;
  driver_id: number;
  user_id: number;
  name: string;
  phone: string;
}

export interface Paginated<T> {
  list: T[];
  total: number;
  page: number;
  page_size: number;
}

/** 运输单状态 */
export const TransitStatusMap: Record<number, string> = {
  0: '待装车',
  1: '运输中',
  2: '已到达',
  3: '已签收',
  4: '已完成',
  5: '已取消',
};

/** 检疫核验状态 */
export const QuarantineStatusMap: Record<number, string> = {
  0: '未核验',
  1: '通过',
  2: '不通过',
  3: '待核验',
};

/** 告警类型 */
export const AlertTypeMap: Record<number, string> = {
  1: '越界',
  2: '偏航',
  3: '超时',
  4: '温湿度',
  5: '证照',
  6: '数据中断',
};

/** 告警级别 */
export const AlertLevelMap: Record<number, string> = {
  1: '黄',
  2: '橙',
  3: '红',
};

/** 告警处理状态 */
export const AlertHandleMap: Record<number, string> = {
  0: '待处理',
  1: '处理中',
  2: '已解除',
  3: '已移交',
};

/** 围栏类型 */
export const FenceTypeMap: Record<number, string> = {
  1: '起运点',
  2: '途经点',
  3: '目的地',
};

/** 证照核验状态 */
export const CertVerifyMap: Record<number, string> = {
  0: '待核验',
  1: '通过',
  2: '不通过',
  3: '待人工复核',
};

/** 证照类型 */
export const CertTypeMap: Record<number, string> = {
  1: 'A证(跨省)',
  2: 'B证(省内)',
};

/** 档案状态 */
export const ArchiveStatusMap: Record<number, string> = {
  0: '待上链',
  1: '已上链',
  2: '失败',
};

export interface TransitOrder {
  id: number;
  transit_no: string;
  order_id: number;
  source_order_no?: string; // C-03 来源交易订单号（order_id>0 时后端联查 trade_orders）
  consignor_id: number;
  vehicle_id: number;
  driver_id: number;
  origin_city: string;
  destination_city: string;
  origin_lng: number;
  origin_lat: number;
  dest_lng: number;
  dest_lat: number;
  cattle_count: number;
  quarantine_status: number;
  status: number;
  alert_level: number;
  last_lng: number;
  last_lat: number;
  last_speed: number;
  last_gps_time: string | null;
  archive_hash: string;
  track_hash: string;
  sign_hash: string;
  actual_depart_time: string | null;
  actual_arrive_time: string | null;
  sign_photo_url: string;
  sign_remark: string;
  signed_at: string | null;
  signed_by: number;
  remark: string;
  created_at: string;
  updated_at: string;
  vehicle?: TransitVehicle;
  driver?: TransitDriver;
  alerts?: TransitAlert[];
}

export interface TransitVehicle {
  id: number;
  owner_id: number;
  plate_no: string;
  vehicle_type: string;
  capacity_kg: number;
  cattle_capacity: number;
  license_no: string;
  transport_license_no: string;
  gps_authorized: number;
  status: number;
  remark: string;
  created_at: string;
}

export interface TransitDriver {
  id: number;
  user_id: number;
  owner_id: number;
  name: string;
  phone: string;
  license_no: string;
  license_type: string;
  status: number;
  remark: string;
  created_at: string;
}

export interface TransitAlert {
  id: number;
  transit_id: number;
  vehicle_id: number;
  alert_type: number;
  alert_level: number;
  alert_content: string;
  lng: number;
  lat: number;
  handle_status: number;
  handle_by: number;
  handle_remark: string;
  handled_at: string | null;
  created_at: string;
}

export interface TransitFence {
  id: number;
  transit_id: number;
  fence_type: number;
  fence_name: string;
  center_lng: number;
  center_lat: number;
  radius_m: number;
  deviation_threshold_m: number;
  polyline: string;
  is_active: number;
  created_at: string;
}

export interface QuarantineCert {
  id: number;
  transit_id: number;
  cert_type: number;
  cert_no: string;
  cert_url: string;
  cattle_count: number;
  verify_status: number;
  verify_result: string;
  verify_by: number;
  verified_at: string | null;
  source: string;
  created_at: string;
}

export interface TransitArchive {
  id: number;
  transit_id: number;
  transit_no: string;
  track_hash: string;
  cert_hash: string;
  sign_hash: string;
  tx_hash: string;
  chain_name: string;
  block_number: number;
  block_time: string | null;
  status: number;
  created_at: string;
}

export interface GPSPoint {
  id?: number;
  transit_id: number;
  vehicle_id: number;
  lng: number;
  lat: number;
  speed: number;
  direction: number;
  temperature?: number | null;
  humidity?: number | null;
  gps_time: string;
}

export interface VehiclePosition {
  transit_id: number;
  transit_no: string;
  plate_no: string;
  lng: number;
  lat: number;
  speed: number;
  status: string;
  alert_level: number;
  last_gps_time?: string | null;
}

export interface DashboardData {
  in_transit_count: number;
  alert_statistics: Record<string, number>;
  vehicle_list: VehiclePosition[];
  recent_orders: TransitOrder[];
}

export interface EnterpriseDashboardData {
  in_transit_count: number;
  today_departed: number;
  today_arrived: number;
  pending_sign: number;
  alert_count: number;
  vehicle_list: VehiclePosition[];
  recent_orders: TransitOrder[];
}

export interface ReportResult {
  accepted: number;
  duplicated: number;
  rejected: number;
}

export interface QuarantineVerifyResult {
  cert_id: number;
  verify_status: number;
  quarantine_status: number;
  message: string;
}
